import { ApolloClient } from '@apollo/client'
import { GotsGetChatMessagesDocument, GotsGetChatMessagesQuery } from './queries.generated'
import { type GotsSubscriptionDocuments } from './hooks/useGotsChatSubscription'

export interface CacheStreamedMessageParams {
  apolloClient: ApolloClient<object>
  chatSessionId: string
  messagePairId: string
  userMessage: string
  oracleMessage: string
  /**
   * Documents (citations / link cards) attached to the oracle reply.  Optional
   * because some callers (e.g. video auto-summary) cache messages that have
   * no documents.  The body already normalizes null/undefined to `[]` before
   * writing the cache.
   */
  documents?: GotsSubscriptionDocuments | null
  language: string
}

/**
 * Adds a completed streaming message pair to the Apollo cache.
 * This allows the OracleGuideOnSideContent component to automatically
 * render it as a historical message without a network fetch.
 */
export const cacheStreamedMessage = ({
  apolloClient,
  chatSessionId,
  messagePairId,
  userMessage,
  oracleMessage,
  documents,
  language,
}: CacheStreamedMessageParams): void => {
  // Build cache-shaped documents array with __typename
  const cacheDocuments = (documents || []).map((doc) => ({
    __typename: 'OracleDocument' as const,
    componentId: doc.componentId,
    componentType: doc.componentType,
    contentLink: doc.contentLink ? {
      __typename: 'ContentLinkDocument' as const,
      contentType: doc.contentLink.contentType,
      contentId: doc.contentLink.contentId,
      title: doc.contentLink.title,
      path: doc.contentLink.path,
    } : null,
  }))

  // Build message pair for cache
  const messagePair = {
    __typename: 'OracleMessagePair' as const,
    id: messagePairId,
    createdAt: new Date().toISOString(),
    thumbsValue: 'none',
    userMessage: {
      __typename: 'OracleMessage' as const,
      content: userMessage,
    },
    oracleMessage: {
      __typename: 'OracleMessage' as const,
      content: oracleMessage,
    },
    documents: cacheDocuments,
  }

  // Must match query variables in OracleGuideOnSideContent.tsx
  const queryVariables = {
    chatSessionId,
    limit: 10,
    offset: 0,
    order: [['createdAt', 'DESC']],
    language: [language],
  }

  const existingData = apolloClient.cache.readQuery<GotsGetChatMessagesQuery>({
    query: GotsGetChatMessagesDocument,
    variables: queryVariables,
  })

  if (existingData?.oracleMessagePairList) {
    const existingMessages = existingData.oracleMessagePairList.oracleMessagePairs

    // Dedupe by ID (not content - avoids OraclePage's brittle pattern)
    const alreadyExists = existingMessages.some((msg) => msg.id === messagePairId)
    if (alreadyExists) return

    // Insert at beginning (DESC order - newest first)
    apolloClient.cache.writeQuery({
      query: GotsGetChatMessagesDocument,
      variables: queryVariables,
      data: {
        oracleMessagePairList: {
          ...existingData.oracleMessagePairList,
          oracleMessagePairs: [messagePair, ...existingMessages],
        },
      },
    })
  } else {
    // No existing cache (first message in session) - create fresh entry
    apolloClient.cache.writeQuery({
      query: GotsGetChatMessagesDocument,
      variables: queryVariables,
      data: {
        oracleMessagePairList: {
          __typename: 'OracleMessagePairListResponse' as const,
          oracleMessagePairs: [messagePair],
        },
      },
    })
  }
}
